import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';
import { storage } from './config';
import { ref, uploadBytes, getDownloadURL, deleteObject, getBytes } from 'firebase/storage';
import { recordDocumentMetadata, createStorageMetadata, type DocumentUploadContext } from './documentRegistry';
import { sanitizeFileName } from '@/utils/fileNameSanitizer';
import { hasClientSideEncryption } from '@/utils/enterpriseSecurity';
import { encryptDocument, decryptDocument, arrayBufferToBlob, blobToFile, type EncryptionMetadata } from '@/services/documentEncryption';

export interface AssetFile {
  id?: string;
  assetId: string; // Reference to asset
  organizationId: string;
  
  // File Information
  fileName: string;
  fileSize: number;
  fileType: string; // MIME type
  storagePath: string; // Firebase Storage path
  storageUrl: string; // Download URL
  
  // Encryption Metadata (Enterprise Plan Only)
  encryptionMetadata?: EncryptionMetadata;
  encryptionEnabled?: boolean; // Quick flag for filtering
  
  // Metadata
  uploadedBy?: string; // User ID
  uploadedByName?: string;
  uploadedByEmail?: string;
  uploadedAt?: any;
  
  // Processing Status
  status: 'pending' | 'processing' | 'analyzed' | 'error';
  analyzedAt?: any;
  error?: string;
  
  createdAt?: any;
  updatedAt?: any;
}

export interface AssetFileUploadOptions {
  organizationId?: string;
  uploadedBy?: { id: string; name: string; email: string };
  onProgress?: (status: 'encrypting' | 'uploading' | 'complete', message?: string) => void;
}

// Helper function to remove undefined values from an object (Firestore doesn't accept undefined)
// Preserves Firestore FieldValue objects (like serverTimestamp())
const removeUndefined = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  // Preserve Firestore FieldValue objects (they have _methodName property)
  if (typeof obj === 'object' && obj._methodName) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined);
  }
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (obj[key] !== undefined) {
        cleaned[key] = removeUndefined(obj[key]);
      }
    }
    return cleaned;
  }
  return obj;
};

export const assetFileService = {
  // Upload a file for an asset
  uploadAssetFile: async (
    assetId: string,
    file: File,
    options: AssetFileUploadOptions = {}
  ): Promise<{ success: boolean; data?: AssetFile; error?: string }> => {
    try {
      if (!storage) {
        throw new Error('Firebase Storage not initialized');
      }

      if (!options.organizationId) {
        throw new Error('Organization ID is required');
      }

      const timestamp = Date.now();
      const sanitizedFileName = sanitizeFileName(file.name);
      const storagePath = `asset_files/${options.organizationId}/${assetId}/${timestamp}_${sanitizedFileName}`;
      
      console.log('📤 Uploading asset file:', {
        assetId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        storagePath
      });

      // Check if Enterprise encryption is enabled
      const encryptionEnabled = await hasClientSideEncryption(options.organizationId);
      
      let fileToUpload: File = file;
      let encryptionMetadata: EncryptionMetadata | undefined;
      const originalFileSize = file.size;

      // Encrypt file if Enterprise plan
      if (encryptionEnabled) {
        console.log('🔒 Encrypting asset file for Enterprise customer...');
        options.onProgress?.('encrypting', 'Encrypting file...');
        
        try {
          const encrypted = await encryptDocument(file, options.organizationId);
          
          // Convert encrypted ArrayBuffer to Blob, then to File
          const encryptedBlob = arrayBufferToBlob(encrypted.encryptedData, file.type);
          fileToUpload = blobToFile(encryptedBlob, file.name, file.type);
          
          // Store encryption metadata for later decryption
          encryptionMetadata = encrypted.metadata;
          
          console.log('✅ Asset file encrypted successfully');
          options.onProgress?.('encrypting', 'Encryption successful');
        } catch (encryptionError: any) {
          console.error('❌ Error encrypting asset file:', encryptionError);
          
          // CRITICAL: For Enterprise customers, encryption failure = upload failure
          // This is required for GLBA compliance - we cannot store unencrypted data
          const errorMessage = encryptionError?.message || 'Encryption failed';
          throw new Error(`Encryption failed: ${errorMessage}. Upload cancelled to maintain GLBA compliance.`);
        }
      }
      
      // Update progress to uploading
      options.onProgress?.('uploading', 'Uploading file...');

      // Create storage reference
      const storageRef = ref(storage, storagePath);
      const uploadedAt = new Date().toISOString();

      // Create storage context for metadata
      const storageContext: DocumentUploadContext = {
        docType: 'asset_file',
        organizationId: options.organizationId,
        assetId: assetId,
        uploadedBy: options.uploadedBy,
        title: file.name,
        source: 'web',
      };

      // Upload file to Storage (encrypted if Enterprise, otherwise original)
      await uploadBytes(
        storageRef,
        fileToUpload,
        createStorageMetadata(storageContext, {
          contentType: file.type,
          uploadedAt,
          // Add encryption metadata to Storage metadata if Enterprise
          ...(encryptionMetadata && {
            customMetadata: {
              encryptionEnabled: 'true',
              wrappedDEK: encryptionMetadata.wrappedDEK,
              iv: encryptionMetadata.iv,
              algorithm: encryptionMetadata.algorithm,
              kmsKeyPath: encryptionMetadata.kmsKeyPath,
              ...(encryptionMetadata.kmsKeyVersion && {
                kmsKeyVersion: encryptionMetadata.kmsKeyVersion,
              }),
            },
          }),
        })
      );

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Record document metadata
      await recordDocumentMetadata({
        storagePath: storageRef.fullPath,
        bucket: (storageRef as any).bucket,
        downloadUrl: downloadURL,
        mimeType: file.type,
        sizeBytes: originalFileSize, // Store original file size (before encryption)
        uploadedAt,
        context: storageContext,
      });

      // Create AssetFile record in Firestore
      const assetFilesRef = collection(db, 'asset_files');
      const assetFileData = removeUndefined({
        assetId,
        organizationId: options.organizationId,
        fileName: file.name,
        fileSize: originalFileSize, // Store original file size (before encryption)
        fileType: file.type,
        storagePath: storageRef.fullPath,
        storageUrl: downloadURL,
        // Add encryption metadata if Enterprise
        ...(encryptionMetadata && {
          encryptionMetadata: {
            wrappedDEK: encryptionMetadata.wrappedDEK,
            iv: encryptionMetadata.iv,
            algorithm: encryptionMetadata.algorithm,
            kmsKeyPath: encryptionMetadata.kmsKeyPath,
            ...(encryptionMetadata.kmsKeyVersion && {
              kmsKeyVersion: encryptionMetadata.kmsKeyVersion,
            }),
          },
          encryptionEnabled: true,
        }),
        uploadedBy: options.uploadedBy?.id,
        uploadedByName: options.uploadedBy?.name,
        uploadedByEmail: options.uploadedBy?.email,
        uploadedAt: serverTimestamp(),
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const docRef = await addDoc(assetFilesRef, assetFileData);
      const assetFile: AssetFile = {
        id: docRef.id,
        ...assetFileData,
      };

      console.log('✅ Asset file uploaded successfully:', docRef.id);
      
      // Notify completion
      options.onProgress?.('complete', 'Upload successful');

      return { success: true, data: assetFile };
    } catch (error: any) {
      console.error('❌ Error uploading asset file:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all files for an asset
  getAssetFiles: async (
    assetId: string,
    organizationId?: string
  ): Promise<{ success: boolean; data?: AssetFile[]; error?: string }> => {
    try {
      const assetFilesRef = collection(db, 'asset_files');
      
      let q;
      if (organizationId) {
        q = query(
          assetFilesRef,
          where('assetId', '==', assetId),
          where('organizationId', '==', organizationId),
          orderBy('uploadedAt', 'desc')
        );
      } else {
        q = query(
          assetFilesRef,
          where('assetId', '==', assetId),
          orderBy('uploadedAt', 'desc')
        );
      }

      const querySnapshot = await getDocs(q);
      const files: AssetFile[] = [];
      
      querySnapshot.forEach((doc) => {
        files.push({ id: doc.id, ...doc.data() } as AssetFile);
      });

      return { success: true, data: files };
    } catch (error: any) {
      console.error('Error getting asset files:', error);
      return { success: false, error: error.message };
    }
  },

  // Get a single asset file by ID
  getAssetFile: async (fileId: string): Promise<{ success: boolean; data?: AssetFile; error?: string }> => {
    try {
      const assetFileRef = doc(db, 'asset_files', fileId);
      const docSnap = await getDoc(assetFileRef);
      
      if (docSnap.exists()) {
        return { success: true, data: { id: docSnap.id, ...docSnap.data() } as AssetFile };
      } else {
        return { success: false, error: 'Asset file not found' };
      }
    } catch (error: any) {
      console.error('Error getting asset file:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete an asset file
  deleteAssetFile: async (
    fileId: string,
    organizationId?: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      // Get file record first to get storage path
      const fileResult = await assetFileService.getAssetFile(fileId);
      if (!fileResult.success || !fileResult.data) {
        return { success: false, error: 'Asset file not found' };
      }

      const assetFile = fileResult.data;

      // Verify organization ID if provided
      if (organizationId && assetFile.organizationId !== organizationId) {
        return { success: false, error: 'Unauthorized: File belongs to different organization' };
      }

      // Delete from Storage
      if (storage && assetFile.storagePath) {
        try {
          const storageRef = ref(storage, assetFile.storagePath);
          await deleteObject(storageRef);
          console.log('✅ File deleted from Storage:', assetFile.storagePath);
        } catch (storageError: any) {
          console.warn('⚠️ Error deleting file from Storage (continuing with Firestore deletion):', storageError);
          // Continue with Firestore deletion even if Storage deletion fails
        }
      }

      // Delete from Firestore
      const assetFileRef = doc(db, 'asset_files', fileId);
      await deleteDoc(assetFileRef);

      console.log('✅ Asset file deleted successfully:', fileId);
      return { success: true };
    } catch (error: any) {
      console.error('❌ Error deleting asset file:', error);
      return { success: false, error: error.message };
    }
  },

  // Update asset file status (for processing/analysis)
  updateAssetFileStatus: async (
    fileId: string,
    status: AssetFile['status'],
    error?: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const assetFileRef = doc(db, 'asset_files', fileId);
      const updateData: any = {
        status,
        updatedAt: serverTimestamp(),
      };

      if (status === 'analyzed') {
        updateData.analyzedAt = serverTimestamp();
      }

      if (error) {
        updateData.error = error;
      }

      await updateDoc(assetFileRef, removeUndefined(updateData));
      return { success: true };
    } catch (error: any) {
      console.error('Error updating asset file status:', error);
      return { success: false, error: error.message };
    }
  },

  // Download asset file (with decryption for Enterprise customers)
  downloadAssetFile: async (
    fileId: string,
    organizationId: string
  ): Promise<{ success: boolean; data?: File; error?: string }> => {
    try {
      // Get file record from Firestore
      const fileResult = await assetFileService.getAssetFile(fileId);
      if (!fileResult.success || !fileResult.data) {
        return { success: false, error: 'Asset file not found' };
      }

      const assetFile = fileResult.data;

      // Verify organization ID
      if (assetFile.organizationId !== organizationId) {
        return { success: false, error: 'Unauthorized: File belongs to different organization' };
      }

      if (!storage || !assetFile.storagePath) {
        return { success: false, error: 'Storage not initialized or file path missing' };
      }

      // Download encrypted file from Storage
      const storageRef = ref(storage, assetFile.storagePath);
      const encryptedBytes = await getBytes(storageRef);
      
      // Convert to ArrayBuffer
      // Firebase Storage getBytes() returns Uint8Array at runtime
      // Use 'any' to bypass TypeScript's strict type checking
      const bytes: any = encryptedBytes;
      let encryptedArrayBuffer: ArrayBuffer;
      
      if (bytes instanceof ArrayBuffer) {
        // Already an ArrayBuffer
        encryptedArrayBuffer = bytes;
      } else if (bytes instanceof Uint8Array) {
        // It's a Uint8Array, convert to ArrayBuffer
        // Create a new ArrayBuffer by copying the data
        encryptedArrayBuffer = bytes.buffer.slice(
          bytes.byteOffset,
          bytes.byteOffset + bytes.byteLength
        ) as ArrayBuffer;
      } else if (bytes && typeof bytes.buffer !== 'undefined') {
        // Fallback: assume it's Uint8Array-like and try to access buffer property
        encryptedArrayBuffer = bytes.buffer.slice(
          bytes.byteOffset || 0,
          (bytes.byteOffset || 0) + (bytes.byteLength || 0)
        ) as ArrayBuffer;
      } else {
        // Last resort: wrap in new Uint8Array
        const wrapped = new Uint8Array(bytes);
        encryptedArrayBuffer = wrapped.buffer.slice(
          wrapped.byteOffset,
          wrapped.byteOffset + wrapped.byteLength
        ) as ArrayBuffer;
      }

      // Check if encryption is enabled
      const encryptionEnabled = await hasClientSideEncryption(organizationId);

      let decryptedFile: File;

      if (encryptionEnabled && assetFile.encryptionMetadata) {
        // Enterprise customer: Decrypt file
        console.log('🔓 Decrypting asset file for Enterprise customer...');
        try {
          const decryptedArrayBuffer = await decryptDocument(
            encryptedArrayBuffer,
            assetFile.encryptionMetadata,
            organizationId
          );

          // Convert decrypted ArrayBuffer to Blob, then to File
          const decryptedBlob = arrayBufferToBlob(decryptedArrayBuffer, assetFile.fileType);
          decryptedFile = blobToFile(decryptedBlob, assetFile.fileName, assetFile.fileType);
          
          console.log('✅ Asset file decrypted successfully');
        } catch (decryptionError: any) {
          console.error('❌ Error decrypting asset file:', decryptionError);
          return { success: false, error: `Decryption failed: ${decryptionError.message}` };
        }
      } else {
        // Non-Enterprise customer: Return file as-is
        const blob = new Blob([encryptedBytes], { type: assetFile.fileType || 'application/octet-stream' });
        decryptedFile = blobToFile(blob, assetFile.fileName, assetFile.fileType);
      }

      return { success: true, data: decryptedFile };
    } catch (error: any) {
      console.error('❌ Error downloading asset file:', error);
      return { success: false, error: error.message };
    }
  },
};

